import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { mediaUrl } from '../utils/media';
import Navbar from '../components/common/Navbar';
import Sidebar from '../components/common/Sidebar';
import EbookCard from '../components/catalog/EbookCard';
import BookViewer from '../components/catalog/BookViewer';
import api from '../utils/api';
import {
  ArrowDownTrayIcon,
  AcademicCapIcon,
  BookOpenIcon,
  Bars3Icon,
  CalendarIcon,
  TagIcon,
  ShieldCheckIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

export default function LibraryPage() {
  const navigate = useNavigate();
  const [ebooks, setEbooks] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    api.get('/api/library')
      .then((r) => setEbooks(r.data))
      .catch(() => toast.error('Gagal memuat perpustakaan'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = ebooks.filter((e) =>
    e.title.toLowerCase().includes(search.toLowerCase())
  );

  const handleDownload = async () => {
    try {
      const res = await api.get(`/api/library/${selected.order_id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selected.title}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Gagal mengunduh PDF');
    }
  };

  const handleCertificate = () => {
    navigate(`/certificate/${selected.order_id}`);
  };

  const purchaseDate = selected?.purchase_date
    ? new Date(selected.purchase_date).toLocaleDateString('id-ID', { dateStyle: 'long' })
    : null;

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <Navbar title="Perpustakaan Saya" />
      <div className="flex flex-1 overflow-hidden">
        {/* Mobile sidebar toggle */}
        {!sidebarOpen && (
          <button
            onClick={() => setSidebarOpen(true)}
            className="md:hidden fixed top-16 left-2 z-50 bg-gray-900 text-white p-2 rounded-full shadow-lg"
          >
            <Bars3Icon className="w-5 h-5" />
          </button>
        )}

        <Sidebar
          items={filtered}
          selected={selected}
          searchQuery={search}
          onSearch={setSearch}
          onSelect={setSelected}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          renderItem={(e) => <EbookCard ebook={e} hidePrice={true} />}
        />

        <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
          {!selected ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
              <div className="w-24 h-24 rounded-2xl bg-gray-200 flex items-center justify-center mb-4">
                <BookOpenIcon className="w-12 h-12 text-gray-400" />
              </div>
              <h2 className="text-gray-600 text-xl font-medium">Pilih ebook dari perpustakaan</h2>
              <p className="text-gray-400 text-sm mt-2">
                {loading ? 'Memuat...' : `${ebooks.length} ebook dimiliki`}
              </p>
            </div>
          ) : (
            <>
              {/* Header bar */}
              <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
                <button
                  onClick={() => { setSelected(null); setSidebarOpen(true); }}
                  className="md:hidden p-1 rounded hover:bg-gray-100"
                  aria-label="Kembali"
                >
                  <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
                </button>
                {selected.cover_url ? (
                  <img src={mediaUrl(selected.cover_url)} alt={selected.title} className="w-12 h-14 rounded object-cover shadow" />
                ) : (
                  <div className="w-12 h-14 rounded bg-gray-200 flex items-center justify-center">
                    <BookOpenIcon className="w-6 h-6 text-gray-500" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-gray-900 font-semibold text-sm truncate">{selected.title}</p>
                  <p className="text-xs text-gray-500 truncate">{selected.seller_name || 'Penjual'}</p>
                </div>
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-1 shrink-0">
                  <ShieldCheckIcon className="w-3.5 h-3.5" />
                  Sudah dibeli
                </span>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4">
                {/* Actions */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <button
                    onClick={handleDownload}
                    className="inline-flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium px-4 py-2 rounded-lg shadow-sm transition"
                  >
                    <ArrowDownTrayIcon className="w-4 h-4" />
                    Unduh PDF
                  </button>
                  <button
                    onClick={handleCertificate}
                    className="inline-flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-800 text-sm font-medium px-4 py-2 rounded-lg shadow-sm transition"
                  >
                    <AcademicCapIcon className="w-4 h-4" />
                    Lihat Sertifikat
                  </button>
                </div>

                {/* Book viewer */}
                {selected.file_url && (
                  <div className="mb-5">
                    <BookViewer url={new URL(selected.file_url, window.location.origin === 'http://localhost:3002' ? 'http://localhost:8001/' : window.location.origin).href} />
                  </div>
                )}

                {/* Info card */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
                    <h3 className="text-sm font-semibold text-gray-800">Informasi Pembelian</h3>
                  </div>
                  <dl className="divide-y divide-gray-100">
                    <div className="flex items-center gap-3 px-4 py-3">
                      <TagIcon className="w-4 h-4 text-gray-400 shrink-0" />
                      <dt className="text-sm text-gray-500 w-32">Nomor Lisensi</dt>
                      <dd className="text-sm font-mono font-medium text-gray-800">{selected.license_code || '–'}</dd>
                    </div>
                    <div className="flex items-center gap-3 px-4 py-3">
                      <CalendarIcon className="w-4 h-4 text-gray-400 shrink-0" />
                      <dt className="text-sm text-gray-500 w-32">Tanggal Pembelian</dt>
                      <dd className="text-sm font-medium text-gray-800">{purchaseDate || '–'}</dd>
                    </div>
                    <div className="flex items-center gap-3 px-4 py-3">
                      <BookOpenIcon className="w-4 h-4 text-gray-400 shrink-0" />
                      <dt className="text-sm text-gray-500 w-32">Total Pembayaran</dt>
                      <dd className="text-sm font-bold text-gray-900">
                        Rp {Number(selected.amount || selected.price || 0).toLocaleString('id-ID')}
                      </dd>
                    </div>
                    {selected.description && (
                      <div className="px-4 py-3">
                        <dt className="text-sm text-gray-500 mb-1">Deskripsi</dt>
                        <dd className="text-sm text-gray-600">{selected.description}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}